"use client";

import { useState } from "react";
import Link from "next/link";
import { Search, Users, Package, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/fleet/status-badge";
import { useSearch } from "@/lib/hooks/use-search";

export function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { results, isLoading, isDegraded } = useSearch(query);

  // Surface the degraded banner only when the operator has opted in
  // via VITE_SHOW_SEARCH_HEALTH=1. The Postgres fallback is supposed
  // to be invisible to end users — the banner is for ops/QA when
  // intentionally probing the failure path. (Renamed from
  // NEXT_PUBLIC_SHOW_SEARCH_HEALTH when the SPA migrated off Next.)
  const showHealth =
    import.meta.env.VITE_SHOW_SEARCH_HEALTH === "1" && isDegraded;

  const hasResults =
    results.drivers.length > 0 ||
    results.orders.length > 0 ||
    results.loads.length > 0 ||
    results.customers.length > 0 ||
    results.vehicles.length > 0;

  return (
    <div className="relative w-full max-w-sm">
      <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
      <Input
        placeholder="Search everything (orders, loads, customers, drivers, vehicles)..."
        className="pl-9 h-9"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && query.length >= 2 && (
        <div className="absolute top-10 left-0 w-full bg-popover border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {showHealth && (
            <p className="px-4 py-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-b">
              Search degraded — using Postgres fallback.
            </p>
          )}
          {isLoading && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Searching...</p>
          )}
          {!isLoading && !hasResults && (
            <p className="px-4 py-3 text-sm text-muted-foreground">No results for &ldquo;{query}&rdquo;</p>
          )}

          {results.drivers.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-1.5 bg-muted/50">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground">
                  <Users className="size-3" /> Drivers
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Fleet &rarr; Drivers ({results.driver_total})
                </Badge>
              </div>
              {results.drivers.map((d) => (
                <Link
                  key={d.id}
                  href={`/fleet/drivers?id=${d.id}`}
                  className="flex items-center justify-between px-4 py-2 text-sm hover:bg-accent"
                  onClick={() => { setOpen(false); setQuery(""); }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{d.name}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {d.phone || d.email || "Driver"}
                    </span>
                  </div>
                  <StatusBadge status={d.status} />
                </Link>
              ))}
            </div>
          )}

          {results.orders.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-1.5 bg-muted/50">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground">
                  <Package className="size-3" /> Orders
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Orders ({results.order_total})
                </Badge>
              </div>
              {results.orders.map((o) => (
                <Link
                  key={o.id}
                  href={`/orders?id=${o.id}`}
                  className="flex items-center justify-between px-4 py-2 text-sm hover:bg-accent"
                  onClick={() => {
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="font-mono text-xs truncate">
                      {o.external_ref || `${o.id.slice(0, 12)}...`}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {o.requested_method}
                    </span>
                  </div>
                  <StatusBadge status={o.status} />
                </Link>
              ))}
            </div>
          )}

          {results.loads.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-1.5 bg-muted/50">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground">
                  <Truck className="size-3" /> Loads
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Loads ({results.load_total})
                </Badge>
              </div>
              {results.loads.map((l) => (
                <Link
                  key={l.id}
                  href={`/loads?id=${l.id}`}
                  className="flex items-center justify-between px-4 py-2 text-sm hover:bg-accent"
                  onClick={() => {
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="font-mono text-xs truncate">
                      {l.id.slice(0, 12)}...
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {l.delivery_method}
                    </span>
                  </div>
                  <StatusBadge status={l.status} />
                </Link>
              ))}
            </div>
          )}

          {results.customers.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-1.5 bg-muted/50">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground">
                  <Users className="size-3" /> Customers
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Customers ({results.customer_total})
                </Badge>
              </div>
              {results.customers.map((c) => (
                <Link
                  key={c.id}
                  href={`/customers?id=${c.id}`}
                  className="flex items-center justify-between px-4 py-2 text-sm hover:bg-accent"
                  onClick={() => {
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{c.name}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {c.external_id || c.contact_email}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {results.vehicles.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-1.5 bg-muted/50">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground">
                  <Truck className="size-3" /> Vehicles
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Fleet &rarr; Vehicles ({results.vehicle_total})
                </Badge>
              </div>
              {results.vehicles.map((v) => (
                <Link
                  key={v.id}
                  href={`/fleet/vehicles?id=${v.id}`}
                  className="flex items-center justify-between px-4 py-2 text-sm hover:bg-accent"
                  onClick={() => { setOpen(false); setQuery(""); }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{v.name}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {v.class} &middot; {v.license_plate}
                    </span>
                  </div>
                  <StatusBadge status={v.is_active ? "active" : "inactive"} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
